import { closeCorpRefusal, notifyCorpRefusal, PAYER_SWITCH_SYMBOLS } from "@/components/OrderForm/corpRefusal";
import { companyLinkService } from "@/services/CompanyLinkService";
import { clientVehicleService } from "@/services/ClientVehicleService";
import { ChargeLevel, OrderStatus } from "@/types/order/order.common";
import { b2bErrorCode, b2bErrorText, onB2BError } from "@/utils/b2bErrors";
import { B2B_ENABLED } from "@/utils/featureFlags";
import { onError } from "@/utils/onError";
import { getAvailableOrderStatuses } from "@/utils/orderHelpers";
import { submitOrderCancel } from "@/utils/orderCancel";
import dayjs from "dayjs";
import { makeAutoObservable, runInAction } from "mobx";
import { notification } from "antd";
import { TableSelectStore } from "@/components/TableSelect/TableSelectStore";
import { DataTableStore } from "./DataTableStore";
import { AddressInfo } from "@/types/zAddressInfo";
import { SearchFilter } from "@/components/filters";
/** FR-007: значение переключателя плательщика «Физлицо»; иначе в нём id компании. */
export const PRIVATE_PAYER = "PRIVATE";
/**
 * SingleOrderStore - manages individual order operations (CRUD, forms)
 * Following SOLID principles and project patterns
 * - Single Responsibility: Only manages individual order operations
 * - Dependency Inversion: Depends on abstractions (interfaces) not concrete implementations
 * - Open/Closed: Easy to extend with new functionality
 */
export class SingleOrderStore {
    constructor(orderService, clientService, tariffService, technicianService) {
        this.orderService = orderService;
        this.clientService = clientService;
        this.tariffService = tariffService;
        this.technicianService = technicianService;
        this.isLoading = false;
        /**
         * Идёт сохранение формы. Отдельно от `isLoading`: на `isLoading` страница заменяется скелетоном и форма
         * размонтируется — при отказе сервера введённые данные терялись бы (FR-007, BR-ORD-07).
         */
        this.isSaving = false;
        this.isCancelling = false;
        this.isEditOpen = false;
        /**
         * FR-007 (UC-103): варианты «платит компания» для выбранного клиента — активные привязки с `canOrder`.
         * Срок привязки относительно плановой даты сверяет форма: дата меняется в ней.
         */
        this.payerOptions = [];
        this.payerOptionsClientId = null;
        /** Автомобили клиента формы (WP-DSP-FIX-02, D31б). */
        this.clientVehicles = [];
        this.clientVehiclesClientId = null;
        this.isClientVehiclesLoading = false;
        this.isPayerOptionsLoading = false;
        /** `null` — плательщик не выбран (предвыбора нет, DEC-004); `PRIVATE_PAYER` или id компании. */
        this.selectedPayer = null;
        /** Последний отказ корп-правила при сохранении (`corp-orderform-error`). */
        this.saveError = null;
        /** Растёт при уходе со страницы: ответ сохранения, пришедший позже, не пишет в новую форму. */
        this.formSession = 0;
        this.order = null;
        this.orderPhotos = null;
        this.clientSelectStore = null;
        this.tariffSelectStore = null;
        this.technicianSelectStore = null;
        makeAutoObservable(this);
    }
    // === INDIVIDUAL ORDER OPERATIONS ===
    /**
     * Load a specific order
     */
    async loadOrderData(orderId) {
        const [order, orderPhotos] = await Promise.all([
            this.orderService.getOrder(orderId),
            this.orderService.getOrderPhotos(orderId),
        ]);
        this.setOrder(order);
        this.setOrderPhotos(orderPhotos);
    }
    async loadOrder(orderId) {
        this.setIsLoading(true);
        try {
            await this.loadOrderData(orderId);
        }
        catch (error) {
            onError(error, "Failed to load order");
        }
        finally {
            this.setIsLoading(false);
        }
    }
    /**
     * Delete an order
     */
    async deleteOrder(orderId) {
        this.setIsLoading(true);
        try {
            await this.orderService.deleteOrder(orderId);
            this.setOrder(null);
            this.setOrderPhotos(null);
            return true;
        }
        catch (error) {
            // FR-007 (UC-107): CP013 — текстом из карты, без сырого текста Postgres
            onB2BError(error, "Не удалось удалить заказ");
            return false;
        }
        finally {
            this.setIsLoading(false);
        }
    }
    /**
     * Change order status
     */
    async changeOrderStatus(newStatus, reason, technicianId) {
        this.setIsLoading(true);
        if (!this.order) {
            throw new Error("Order not found");
        }
        try {
            await this.orderService.changeOrderStatus(this.order.id, newStatus, reason, technicianId);
            await this.loadOrder(this.order.id);
            notification.success({
                message: "Статус заказа успешно изменен",
            });
        }
        catch (error) {
            // FR-007 (UC-105): CP009 / CP010 / CP012 — текстом из карты
            onB2BError(error, "Не удалось изменить статус заказа");
        }
        finally {
            this.setIsLoading(false);
        }
    }
    /**
     * WP-DSP-FIX-03: отмена заказа с причиной. Инициатор определяет статус (диспетчер → DISPATCHER_CANCELLED,
     * клиент → CANCELLED); история статусов с причиной пишется changeOrderStatus. Карточка перечитывается
     * без скелетона. Возвращает текст ошибки (корп-отказы — из карты B2B) или null при успехе.
     */
    async cancelOrder(initiator, reason) {
        const order = this.order;
        if (!order)
            return "Заказ не найден";
        this.isCancelling = true;
        try {
            await submitOrderCancel((id, status, cancelReason) => this.orderService.changeOrderStatus(id, status, cancelReason), order.id, initiator, reason);
        }
        catch (error) {
            console.error("Не удалось отменить заказ", error);
            runInAction(() => {
                this.isCancelling = false;
            });
            return b2bErrorText(error, "Не удалось отменить заказ");
        }
        // Отмена уже записана — ошибка перечитывания не превращает её в «не удалось отменить»
        try {
            await this.loadOrderData(order.id);
        }
        catch (error) {
            onError(error, "Заказ отменён, но карточку не удалось обновить");
        }
        finally {
            runInAction(() => {
                this.isCancelling = false;
            });
        }
        return null;
    }
    /**
     * Save order (create or update)
     */
    async saveOrder(values) {
        this.isSaving = true;
        this.saveError = null;
        closeCorpRefusal();
        const session = this.formSession;
        const existing = this.order;
        const task = existing
            ? this.orderService.updateOrder(existing.id, {
                ...existing,
                ...values,
            })
            : this.orderService.createOrder(values);
        try {
            const saved = await task;
            this.setOrder(saved);
            return saved;
        }
        catch (error) {
            if (session === this.formSession)
                this.handleSaveError(error, values, existing);
            else
                console.error("Ответ сохранения пришёл после ухода со страницы", error);
            return null;
        }
        finally {
            runInAction(() => {
                this.isSaving = false;
            });
        }
    }
    /**
     * FR-007: отказ при сохранении. Текст — из карты `b2bErrors` (контекст: создание / перенос даты, у `CP003`
     * два смысла, CCR-010); сырой текст Postgres не показывается. У корп-заказа отказ ещё и остаётся у формы
     * (`corp-orderform-error`), а при создании предлагается «Оформить как физлицо» (DEC-004).
     */
    handleSaveError(error, values, existing) {
        const context = existing ? "orderDateChange" : "orderCreate";
        const fallback = existing ? "Не удалось обновить заказ" : "Не удалось создать заказ";
        const isCorporate = existing ? existing.isCorporate : !!values.corpCompanyId;
        if (!isCorporate) {
            onB2BError(error, fallback, context);
            return;
        }
        console.error(fallback, error);
        const code = b2bErrorCode(error);
        const text = b2bErrorText(error, fallback, context);
        runInAction(() => {
            this.saveError = { code, text };
        });
        if (!existing && code !== null && PAYER_SWITCH_SYMBOLS.has(code)) {
            // Действие привязано к клиенту отказа: после смены клиента оно ничего не выбирает (DEC-004)
            const clientId = this.payerOptionsClientId;
            notifyCorpRefusal(text, () => {
                if (this.payerOptionsClientId === clientId)
                    this.switchToPrivatePayer();
                else
                    closeCorpRefusal();
            });
        }
        else {
            notification.error({ message: "Ошибка", description: text });
        }
    }
    // === PAYER (FR-007, UC-103) ===
    /**
     * Варианты плательщика для клиента формы. Смена клиента сбрасывает выбор: вариант принадлежал прежнему
     * клиенту. Интерфейс только предлагает — активность привязки, статус компании, срок и лимит проверяет триггер.
     */
    /** Автомобили выбранного клиента — список поля «Автомобиль» (D31б). Смена клиента сбрасывает список. */
    async loadClientVehicles(clientId) {
        if (clientId === this.clientVehiclesClientId)
            return;
        this.clientVehiclesClientId = clientId;
        this.clientVehicles = [];
        if (!clientId)
            return;
        this.isClientVehiclesLoading = true;
        try {
            const vehicles = await clientVehicleService.listByClient(clientId);
            runInAction(() => {
                if (this.clientVehiclesClientId === clientId)
                    this.clientVehicles = vehicles;
            });
        }
        catch (error) {
            onError(error, "Не удалось загрузить автомобили клиента");
        }
        finally {
            runInAction(() => {
                if (this.clientVehiclesClientId === clientId)
                    this.isClientVehiclesLoading = false;
            });
        }
    }
    async loadPayerOptions(clientId) {
        if (clientId === this.payerOptionsClientId)
            return;
        this.payerOptionsClientId = clientId;
        this.payerOptions = [];
        this.selectedPayer = null;
        this.saveError = null;
        closeCorpRefusal();
        this.isPayerOptionsLoading = false;
        if (!B2B_ENABLED || !clientId)
            return;
        this.isPayerOptionsLoading = true;
        try {
            const links = await companyLinkService.getClientLinks(clientId, "ACTIVE");
            runInAction(() => {
                if (this.payerOptionsClientId !== clientId)
                    return;
                // Q2: вариант с can_order = false скрыт, причина не раскрывается
                this.payerOptions = links.filter((link) => link.canOrder === true);
            });
        }
        catch (error) {
            // Неудачная загрузка не считается загруженной. Эффект формы зависит от idClient, поэтому перезапрос будет
            // только после смены значения: выбран другой клиент или поле очищено и клиент выбран заново. Повторный
            // выбор того же клиента без смены значения запроса не даёт — заказ можно оформить как на физлицо.
            runInAction(() => {
                if (this.payerOptionsClientId !== clientId)
                    return;
                this.payerOptionsClientId = null;
                this.isPayerOptionsLoading = false;
            });
            onB2BError(error, "Не удалось загрузить компании клиента — заказ можно оформить как на физлицо");
        }
        finally {
            runInAction(() => {
                if (this.payerOptionsClientId === clientId)
                    this.isPayerOptionsLoading = false;
            });
        }
    }
    selectPayer(payer) {
        this.selectedPayer = payer;
        this.saveError = null;
        closeCorpRefusal();
    }
    /** «Оформить как физлицо»: только переключатель — введённые данные формы не трогаются (BR-ORD-07). */
    switchToPrivatePayer() {
        this.selectPayer(PRIVATE_PAYER);
    }
    resetPayer() {
        closeCorpRefusal();
        this.payerOptions = [];
        this.payerOptionsClientId = null;
        this.isPayerOptionsLoading = false;
        this.selectedPayer = null;
        this.saveError = null;
    }
    // === REFERENCE DATA OPERATIONS ===
    /**
     * Initialize reference data
     */
    async init(orderId) {
        this.setIsLoading(true);
        try {
            if (orderId) {
                await this.loadOrderData(orderId);
            }
            // Создание сторов для выбора клиента, тарифа и техника
            this.setClientSelectStore(new TableSelectStore(new DataTableStore({
                dataLoader: {
                    loadData: (params) => this.clientService.getClientsWithParams(params),
                },
            }), {
                dataLoader: () => this.order
                    ? this.clientService.getClientsByIds([this.order.idClient])
                    : Promise.resolve([]),
            }, [
                {
                    id: "client-search-filter",
                    Cmp: SearchFilter,
                    props: {
                        name: "search",
                        label: "Поиск",
                        placeholder: "Поиск по имени, email, телефону...",
                        allowClear: true,
                        showSearchIcon: true,
                        onChange: (value) => this.clientSelectStore?.filterStore?.updateFilter("search", value),
                    },
                    visible: true,
                    order: 1,
                },
            ]));
            this.setTariffSelectStore(new TableSelectStore(new DataTableStore({
                dataLoader: {
                    loadData: (params) => this.tariffService.getTariffsWithParams(params),
                },
            }), {
                dataLoader: () => this.order
                    ? this.tariffService.getTariffsByIds([this.order.idTariff])
                    : Promise.resolve([]),
            }, [
                {
                    id: "tariff-search-filter",
                    Cmp: SearchFilter,
                    props: {
                        name: "search",
                        label: "Поиск",
                        placeholder: "Поиск по названию, типу...",
                        allowClear: true,
                        showSearchIcon: true,
                        onChange: (value) => this.tariffSelectStore?.filterStore?.updateFilter("search", value),
                    },
                    visible: true,
                    order: 1,
                },
            ]));
            this.setTechnicianSelectStore(new TableSelectStore(new DataTableStore({
                dataLoader: {
                    loadData: (params) => this.technicianService.getTechniciansWithParams(params),
                },
            }), {}, [
                {
                    id: "technician-search-filter",
                    Cmp: SearchFilter,
                    props: {
                        name: "search",
                        label: "Поиск",
                        placeholder: "Поиск по имени, email...",
                        allowClear: true,
                        showSearchIcon: true,
                        onChange: (value) => this.technicianSelectStore?.filterStore?.updateFilter("search", value),
                    },
                    visible: true,
                    order: 1,
                },
            ]));
        }
        catch (error) {
            onError(error, "Failed to load reference data");
        }
        finally {
            this.setIsLoading(false);
        }
    }
    get currentOrderClient() {
        return this.clientSelectStore?.pagedData.find((client) => client.id === this.order?.idClient);
    }
    get availableStatuses() {
        const validStatuses = this.order
            ? getAvailableOrderStatuses(this.order.status, this.order.isCorporate)
            : [];
        return validStatuses;
    }
    // === UTILITY METHODS ===
    /**
     * Get initial form data for order creation/editing
     */
    get initialFormData() {
        if (this.isLoading)
            return {};
        return this.order
            ? { ...this.order, idVehicle: this.order.vehicleInfo?.idVehicle ?? null }
            : {
                status: OrderStatus.NEW,
                orderNumber: "",
                idSubscription: null,
                idClient: "",
                idTariff: "",
                scheduledDate: dayjs().add(1, "day"),
                chargeLevel: ChargeLevel.TO_MAXIMUM,
                chargeValue: null,
                addressInfo: AddressInfo.fromPlainObject({
                    latitude: 0,
                    longitude: 0,
                    addressShort: "",
                    addressLocality: "",
                }),
                currentCharge: null,
            };
    }
    /**
     * Clear order-specific data
     */
    clearOrderData() {
        this.setOrder(null);
        this.setOrderPhotos(null);
        this.resetPayer();
        this.isSaving = false;
        this.formSession += 1;
        this.clientSelectStore?.reset();
        this.tariffSelectStore?.reset();
        this.technicianSelectStore?.reset();
        this.setIsLoading(false);
    }
    // === PRIVATE SETTERS (following MobX patterns) ===
    setOrder(order) {
        this.order = order;
    }
    setOrderPhotos(orderPhotos) {
        this.orderPhotos = orderPhotos;
    }
    setIsLoading(loading) {
        this.isLoading = loading;
    }
    setClientSelectStore(clientSelectStore) {
        this.clientSelectStore = clientSelectStore;
    }
    setTariffSelectStore(tariffSelectStore) {
        this.tariffSelectStore = tariffSelectStore;
    }
    setTechnicianSelectStore(technicianSelectStore) {
        this.technicianSelectStore = technicianSelectStore;
    }
    canEditOrder() {
        if (!this.order)
            return true;
        return this.order.status === OrderStatus.NEW;
    }
    canDeleteOrder() {
        return !!this.order;
    }
    openEdit() {
        this.isEditOpen = true;
        this.saveError = null;
    }
    closeEdit() {
        this.isEditOpen = false;
        this.saveError = null;
    }
}
